// Dependencies
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Link from "@mui/material/Link";
import { useDispatch, useSelector } from "react-redux";
import { useRealtimeUnits } from 'Core/hooks';
import moment from "moment";

// Components
import SidePanel, {
  SidePanelBody,
  SidePanelFooter,
  SidePanelHeader,
} from "Components/SidePanel";
import { Button, Checkbox, Form, Select } from "Components";
import { Box, Grid } from "@mui/material";
import {
  TimeFrame,
  useTimeFrameComponent,
} from "@gpstrackit/gtc-react-controls";
import Pill from "Components/Pill";
import Label from "Components/uiControls/Label";

// Helpers
import { formatEntities, getInitialRange } from "Modules/video/.globals/helpers";
import { HasPermission } from "Core/security";

// Actions
import {
  getListHasVideoEvents
} from "Redux/actions";

// Styles
import "./styles.scss";
import { useIntl } from "react-intl";
import TagsManage from "../../../../../.common/components/TagsManage";

/**
 * Renders a side menu to create a VideoClip.
 * @param {Object} props - Component props.
 * @returns {React.Node} -The side menu component.
 */

const formatGroupLabel = (data) => (
  <>
    <div
      style={{
        width: "100%",
      }}
    ></div>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px",
      }}
    >
      <span
        style={{
          fontWeight: "bold",
          color: colors["color_black"],
          fontSize: "12px",
        }}
      >
        {data?.label}
      </span>
    </div>
  </>
);

const AdvancedFilters = ({
  setOpen,
  open,
  indexTab,
  tableViewRef,
  onChangeFilters,
  currentFilters,
  isClipRequest,
}) => {
  const dispatch = useDispatch();
  const { messages, locale } = useIntl();
  const defaultFrame = "last7days";

  const responseListEventsVideo = useSelector((state) => state.videoEventsRedux.responseListEventsVideo);
  const loadingEventsVideo = useSelector((state) => state.videoEventsRedux.loadingEventsVideo);
  const units = useRealtimeUnits({ trackFields: ['id', 'name', 'label', 'disabled', 'driverId', 'vehicleStatusId', 'tags'] });
  const user = useSelector((state) => state.securityRedux.user);

  const mediaTypes = useMemo(
    () => [
      { id: "Picture", label: messages["videoMainView_snapshots"] },
      { id: "Clip", label: messages["videoMainView_videoClips"] },
      { id: "All", label: messages["videoMainView_mediaTypeBoth"] },
    ],
    [messages]
  );

  const reviewStates = useMemo(
    () => [
      { id: "Unreviewed", label: messages["videoMainView_unreviewed"] },
      { id: "Accepted", label: messages["videoMainView_accepted"] },
      { id: "Disputed", label: messages["videoMainView_disputed"] },
      { id: "FalsePositive", label: messages["videoMainView_falsePositive"] },
      { id: "Reviewed", label: messages["videoMainView_reviewed"] },
      { id: "All", label: messages["videoMainView_reviewAll"] },
    ],
    [messages]
  );

  const filters = useMemo(
    () => [
      {
        label: messages["videoMainView_all"],
        id: "all",
      },
      {
        label: messages["videoMainView_failedTab"],
        id: "failed",
      },
      {
        label: messages["videoMainView_pendingTab"],
        id: "pending",
      },
      {
        label: messages["videoMainView_completedTab"],
        id: "completed",
      },
      {
        label: messages["videoMainView_inProgressTab"],
        id: "inProgress",
      },
    ],
    [messages]
  );

  const entities = useMemo(
    () => [
      { id: "All", label: messages["clipRequestAdvancedFilters_all"] },
      { id: "Unit", label: messages["clipRequestAdvancedFilters_singleUnits"] }
    ],
    [messages]
  );

  if (!isClipRequest && entities.length === 2) {
    entities.push({ id: "Driver", label: messages["clipRequestAdvancedFilters_drivers"] });
  }

  let enable30DaysFilter = true;
  // if (parentTab === EVENT_KEY && units?.length > 500) {
  //   enable30DaysFilter = false;
  // }

  const timeFrameOptions = [
    { id: "today", label: messages["date_last_today"] },
    { id: "yesterday", label: messages["date_last_yesterday"] },
    { id: "last7days", label: messages["date_last_7days"] },
    { id: "last30Days", label: messages["date_last_30days"] },
    { id: "custom", label: messages["date_custom"] },
  ];

  const advanceFiltersForm = useRef();

  let currentDate = {
    start: moment(filters?.dateRange?.start).toDate(),
    end: moment(filters?.dateRange?.end).toDate(),
  };

  const [selectedEventTypes, setSelectedEventTypes] = useState(
    currentFilters.eventsType || []
  );
  const [optionEventTypes, setOptionsEventTypes] = useState([]);
  const [hasNotes, setHasNotes] = useState(
    currentFilters?.hasNotes != null ? currentFilters?.hasNotes : false
  );
  const [entity, setEntity] = useState(currentFilters?.entity || entities[0]);
  const [selectedItems, setSelectedItems] = useState(
    currentFilters?.items?.length
      ? currentFilters?.items
      : [{ id: -1, name: "All" }]
  );
  const [date, setDate] = useState(currentDate);
  const [statusClip, setStatusClips] = useState(
    currentFilters?.status ? currentFilters?.status : "all"
  );
  const [mediaType, setMediaType] = useState(
    currentFilters?.mediaType ? currentFilters?.mediaType : mediaTypes[2].id
  );
  const [reviewStatus, setReviewStatus] = useState(
    currentFilters?.reviewStatus
      ? currentFilters?.reviewStatus
      : reviewStates[5].id
  );
  const [loadingTags, setLoadingTags] = useState(false);
  const [hasFilters, setHasFilters] = useState(true);

  const timeFrame = useTimeFrameComponent(timeFrameOptions, defaultFrame);

  useEffect(() => {
    if (open) {
      timeFrame.defaultvalue(currentFilters?.timeFrame || defaultFrame);
      timeFrame.onChangeDateRange(moment(currentFilters?.dateRange?.start), moment(currentFilters?.dateRange?.end));
      dispatch(getListHasVideoEvents({}));
    }
  }, [open]);

  useEffect(() => {
    if (responseListEventsVideo?.length) {
      const transformedEvents = responseListEventsVideo.map((event) => {
        let key =
          event?.eventName.replace(/\s+/g, "").charAt(0).toLowerCase() +
          event?.eventName.replace(/\s+/g, "").slice(1);
        let label = messages[`videoMainView_${key}`]
          ? messages[`videoMainView_${key}`]
          : event?.eventName;
        return { key, label, events: [event?.eventName] };
      });
      const orderer = transformedEvents?.sort((a, b) =>
        a.label.localeCompare(b.label)
      );

      setOptionsEventTypes(orderer);
    }
  }, [responseListEventsVideo, locale]);

  useEffect(() => {
    const { startDate, endDate } = timeFrame;
    if (startDate && endDate) setDate({ start: startDate, end: endDate });
  }, [timeFrame?.startDate, timeFrame?.endDate]);

  useEffect(() => {
    if ((selectedEventTypes && selectedEventTypes?.length === 0) || !open)
      return;
    const updateLangPills = selectedEventTypes?.map((item) => {
      let labelKey = messages[`videoMainView_${item?.key}`];
      if (!labelKey) {
        labelKey = item?.label;
      }
      return {
        ...item,
        label: labelKey,
      };
    });
    setSelectedEventTypes(updateLangPills);
  }, [locale, open]);

  const cleanFilters = useCallback(() => {
    if (loadingTags) return;

    const defaultItems = [{ id: -1, name: "All" }];
    const defaultEntity = entities[0];
    const defaultDateRange = getInitialRange(defaultFrame);

    setSelectedItems(defaultItems);
    setSelectedEventTypes([]);
    setEntity(defaultEntity);
    setHasNotes(false);
    setStatusClips("all");
    setMediaType("All");
    setReviewStatus("All");
    timeFrame.reset();
    timeFrame.defaultvalue(defaultFrame);
    setDate(defaultDateRange);

    onChangeFilters && onChangeFilters({
      ...currentFilters,
      events: [],
      entity: defaultEntity,
      entities: formatEntities(defaultItems),
      dateRange: defaultDateRange,
      customDate: defaultDateRange,
      hasNotes: false,
      mediaType: "All",
      timeFrame: defaultFrame,
      eventsType: [],
      reviewStatus: undefined,
      items: defaultItems,
      status: "all",
      offset: 0,
      nextToken: null,
    });
  }, [currentFilters, defaultFrame, entities, loadingTags, onChangeFilters, timeFrame]);

  const onChangeEntityTagPicker = useCallback(
    (items, entity) => {
      if (items?.length === 0) return setSelectedItems([]);
      const newItems = items.map((n) => {
        if (n.name === "Tag") {
          return { ...n, label: entity };
        }

        return n;
      });

      setSelectedItems(newItems);
    },
    [setSelectedItems]
  );

  return (
    <SidePanel
      style={{
        width: 400,
        transform: "translateX(400px)",
      }}
      classSidePanel={hasFilters ? "side-panel-icon-filter" : "side-panel-icon"}
      isOpen={open}
      toggle={() => {
        setOpen && setOpen(!open);
      }}
    >
      <SidePanelHeader>
        <div
          style={{ display: "flex", flexDirection: "column" }}
          className="advanced-filters_modal"
        >
          <Label title>
            {messages["clipRequestAdvancedFilters_advancedFilters"]}
          </Label>
          <Link
            className="clear-all-link"
            underline="always"
            onClick={cleanFilters}
          >
            {messages["clearAll"]}
          </Link>
        </div>
      </SidePanelHeader>

      <SidePanelBody style={{ height: "75%", overflowY: "auto" }}>
        <div className="advanced-filters_modal">
          <Box style={{ width: "97%" }}>
            <Form
              ref={advanceFiltersForm}
              onValidSubmit={useCallback(() => {
                tableViewRef?.current?.setNewPage(1);
                tableViewRef?.current?.setNewRowsPerPage(10);
                if (selectedItems.length > 0) {
                  let events =
                    selectedEventTypes?.reduce(
                      (mappedEvents, currentEvent) => [
                        ...mappedEvents,
                        ...currentEvent.events,
                      ],
                      []
                    ) || [];
                  let newSelectedItems = [...selectedItems];
                  let newFilters = {
                    ...currentFilters,
                    events: events,
                    entity: entity,
                    entities: formatEntities(newSelectedItems),
                    dateRange: date,
                    customDate: date,
                    hasNotes: hasNotes,
                    mediaType: mediaType,
                    timeFrame: timeFrame?.option?.id || defaultFrame,
                    eventsType: selectedEventTypes,
                    reviewStatus:
                      reviewStatus == "All" ? undefined : reviewStatus,
                    items: newSelectedItems,
                    status: statusClip,
                  };
                  onChangeFilters && onChangeFilters(newFilters);
                }
              })}
            >
              <div style={{ marginTop: 5 }}>
                <TagsManage
                  label={messages["clipRequestAdvancedFilterBy"]}
                  entities={entities}
                  onChange={onChangeEntityTagPicker}
                  hideTagsWithoutEntities={true}
                  enableSelectAll={true}
                  disableUntaggedSelection
                  mode="list"
                  height={250}
                  value={selectedItems}
                  includeUnassignedDrivers={false}
                  entitySelected={entity}
                  setEntitySelected={(entity) => {
                    if (entity.id === "All"){
                      setSelectedItems([{ id: -1, name: entity.id }]);
                    } else {
                      setSelectedItems([]);
                    }

                    setEntity(entity);
                  }}
                />
              </div>

              <Grid>
                <Label>{messages["clipRequestAdvancedTimeFrame"]}</Label>
                <div className="time-frame-container">
                  <TimeFrame
                    {...timeFrame}
                    placeholder={messages["reportsMainRT_timeFrame"]}
                    customRangeDate={enable30DaysFilter ? 30 : 15}
                    maxDate={moment()}
                    filterSwitch={false}
                    withTime={true}
                    minDate={moment().subtract(90, "d")}
                    dateFormat={user?.dateformat || "MM/DD/YYYY hh:mm A"}
                    openDateRangePickerModalAutomatically={false}
                  />
                </div>
              </Grid>

              {!isClipRequest && (
                <>
                  <Grid item>
                    <Select
                      id="selectEventTypes"
                      label={messages["videoMainView_eventType"]}
                      value={selectedEventTypes || []}
                      onChange={(items = []) => setSelectedEventTypes(items)}
                      options={optionEventTypes}
                      textField="label"
                      valueField="key"
                      placeholder={messages["select"]}
                      className={"selected-report-options"}
                      formatGroupLabel={formatGroupLabel}
                      titleStyles={"report-setup-type-title"}
                      selectedOptionsBelow
                      multiple
                      isOptionDisabled={(option) => {
                        return !option?.events;
                      }}
                      loading={loadingEventsVideo}
                      disabled={false}
                    />

                    {
                      optionEventTypes?.length === 0 && !loadingEventsVideo ? (
                        <div>
                          <small className="text-danger">{messages['videoMainView_noEventsAvailable']}</small>
                        </div>
                      ) : null
                    }
                  </Grid>

                  <Grid className="clip-request-advanced-filters-checkboxes">
                    <label className="input-normal-label">
                      {messages["clipRequestAdvancedMediaType"]}
                    </label>
                    <div style={{ paddingTop: "10px" }}>
                      {mediaTypes.map((currentMediaType, index) => {
                        return (
                          <Pill
                            key={index}
                            textField={"label"}
                            item={currentMediaType}
                            outlined={currentMediaType.id !== mediaType}
                            onClick={() => setMediaType(currentMediaType.id)}
                            style={{ border: "0" }}
                            label="pill-label-text"
                            colorField="default"
                            colorSelection="005bf8"
                          />
                        );
                      })}
                    </div>
                  </Grid>

                  {HasPermission(user, ["vidrw"], [261]) && (
                    <Grid className="clip-request-advanced-filters-checkboxes">
                      <label className="input-normal-label">
                        {messages["videoMainView_reviewStatus"]}
                      </label>
                      <div style={{ paddingTop: "10px" }}>
                        {reviewStates.map((currentReviewStatus, index) => {
                          if (
                            currentReviewStatus.id === "FalsePositive" &&
                            [1, 2].includes(indexTab)
                          )
                            return <></>;
                          return (
                            <Pill
                              key={index}
                              textField={"label"}
                              item={currentReviewStatus}
                              outlined={currentReviewStatus.id !== reviewStatus}
                              onClick={() =>
                                setReviewStatus(currentReviewStatus.id)
                              }
                              style={{ border: "0" }}
                              label="pill-label-text"
                              colorField="default"
                              colorSelection="005bf8"
                            />
                          );
                        })}
                      </div>
                    </Grid>
                  )}
                </>
              )}

              {isClipRequest && (
                <Grid style={{ marginTop: "20px" }}>
                  <Label>{messages["clipRequestAdvancedStatus"]}</Label>
                  <div style={{ paddingTop: "10px" }}>
                    {filters.map((filter, index) => {
                      return (
                        <Pill
                          key={index}
                          textField={"label"}
                          item={filter}
                          outlined={filter.id !== statusClip}
                          onClick={() => setStatusClips(filter.id)}
                          style={{
                            border: "0",
                            paddingLeft: 31,
                            paddingRight: 31,
                          }}
                          label="pill-label-text"
                          colorField="default"
                          colorSelection="005bf8"
                        />
                      );
                    })}
                  </div>
                </Grid>
              )}
            </Form>
          </Box>
        </div>
      </SidePanelBody>

      <SidePanelFooter>
        <div style={{ width: "65%", display: "flex", justifyContent: "end" }}>
          <Button className="btn-cancel" onClick={() => setOpen(false)}>
            {messages["cancel"]}
          </Button>
          <Button
            disabled={selectedItems?.length === 0}
            className="btn-blue"
            onClick={() => advanceFiltersForm.current.submit()}
          >
            {messages["Apply"]}
          </Button>
        </div>
      </SidePanelFooter>
    </SidePanel>
  );
};

export default AdvancedFilters;
